import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { Can } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";
import AssessmentViewerPicker, {
  parseSingleKey,
} from "./AssessmentViewerPicker";

function linkExcludeKey(link) {
  if (link.organization_user_admin_id) {
    return `admin:${link.organization_user_admin_id}`;
  }
  if (link.organization_user_manager_id) {
    return `manager:${link.organization_user_manager_id}`;
  }
  return null;
}

function viewerDisplayName(link) {
  const name = `${link.viewer_first_name || ""} ${link.viewer_last_name || ""}`.trim();
  if (name) return name;
  return link.viewer_email || link.assessment_viewer_link_id;
}

/**
 * @param {{
 *   organizationId: string,
 *   assessmentId?: string|null,
 *   assessmentAssignmentRequestId?: string|null,
 *   assigneeUserId?: string|null,
 *   allowMutate?: boolean,
 * }} props
 */
export default function AssessmentViewerLinksPanel({
  organizationId,
  assessmentId = null,
  assessmentAssignmentRequestId = null,
  assigneeUserId = null,
  allowMutate = true,
}) {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [addKey, setAddKey] = useState(null);
  const [managing, setManaging] = useState(false);

  const targetReady = !!(assessmentId || assessmentAssignmentRequestId);

  const mutatePermissions = useMemo(() => {
    if (assessmentAssignmentRequestId && !assessmentId) {
      return [
        PERMISSIONS.ASSESSMENTS_APPROVE,
        PERMISSIONS.ASSESSMENTS_ASSIGN,
        PERMISSIONS.ASSESSMENTS_REQUEST,
        PERMISSIONS.ASSESSMENTS_ORG_APPROVE,
      ];
    }
    return [
      PERMISSIONS.ASSESSMENTS_ASSIGN,
      PERMISSIONS.ASSESSMENT_INSTRUMENTS_EDIT,
      PERMISSIONS.ASSESSMENTS_REQUEST,
      PERMISSIONS.ASSESSMENTS_ORG_APPROVE,
    ];
  }, [assessmentAssignmentRequestId, assessmentId]);

  const fetchLinks = useCallback(() => {
    if (!organizationId || !targetReady) {
      setLinks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const body = {
      page: 0,
      page_size: 50,
    };
    if (assessmentId) {
      body.assessment_id = assessmentId;
    } else {
      body.assessment_assignment_request_id = assessmentAssignmentRequestId;
    }

    axios
      .post(
        `/organizations/${organizationId}/retrieve-assessment-viewer-links/`,
        body
      )
      .then(({ data }) => {
        setLinks(data.response[0]?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }, [organizationId, assessmentId, assessmentAssignmentRequestId, targetReady]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const excludeKeys = useMemo(
    () =>
      links
        .map(linkExcludeKey)
        .filter(Boolean)
        .map(String),
    [links]
  );

  function addViewer() {
    const staff = parseSingleKey(addKey);
    if (!staff || !targetReady) return;

    setBusyKey("add");
    const body = { ...staff };
    if (assessmentId) {
      body.assessment_id = assessmentId;
    } else {
      body.assessment_assignment_request_id = assessmentAssignmentRequestId;
    }

    axios
      .post(`/organizations/${organizationId}/assessment-viewer-links/`, body)
      .then(() => {
        setAddKey(null);
        setBusyKey(null);
        setManaging(false);
        toast.success(t("updated"));
        fetchLinks();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function cancelManaging() {
    setAddKey(null);
    setManaging(false);
  }

  function removeViewer(linkId) {
    setBusyKey(`remove-${linkId}`);
    axios
      .delete(
        `/organizations/${organizationId}/assessment-viewer-links/${linkId}/`
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchLinks();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (!targetReady) {
    return null;
  }

  if (loading) {
    return (
      <Group justify="center" py="sm">
        <Loader size="xs" />
      </Group>
    );
  }

  return (
    <Stack gap="xs" mt="sm">
      <Group justify="space-between" align="center">
        <Title order={6}>{t("assessment_viewer_plural")}</Title>
        {allowMutate && !managing && (
          <Can permission={mutatePermissions} mode="any">
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setManaging(true)}
            >
              {t("assessment_viewer_add")}
            </Button>
          </Can>
        )}
      </Group>

      <Stack gap="xs">
        {!links.length && (
          <Text size="sm" c="dimmed">
            {t("assessment_viewer_none")}
          </Text>
        )}
        {links.map((link) => (
          <Card key={link.assessment_viewer_link_id} padding="xs" withBorder>
            <Flex gap="sm" align="center">
              <div style={{ flexGrow: 1 }}>
                <Text size="sm" fw={600}>
                  {viewerDisplayName(link)}
                </Text>
                <Text size="xs" c="dimmed">
                  {link.viewer_email}
                  {link.organization_user_admin_id
                    ? ` · ${t("organization_admin_plural")}`
                    : ""}
                  {link.organization_user_manager_id
                    ? ` · ${t("manager_singular")}`
                    : ""}
                </Text>
              </div>
              {allowMutate && (
                <Can permission={mutatePermissions} mode="any">
                  <ActionIcon
                    size="sm"
                    radius="xl"
                    variant="light"
                    color="red"
                    title={t("remove")}
                    loading={busyKey === `remove-${link.assessment_viewer_link_id}`}
                    onClick={() =>
                      removeViewer(link.assessment_viewer_link_id)
                    }
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Can>
              )}
            </Flex>
          </Card>
        ))}
      </Stack>

      {allowMutate && managing && (
        <Can permission={mutatePermissions} mode="any">
          <Stack gap="sm">
            <Flex gap="sm" align="flex-end">
              <div style={{ flex: 1 }}>
                <AssessmentViewerPicker
                  organizationId={organizationId}
                  userId={assigneeUserId}
                  mode="single"
                  value={addKey}
                  onChange={setAddKey}
                  excludeKeys={excludeKeys}
                />
              </div>
              <Button
                leftSection={<IconPlus size={14} />}
                disabled={!addKey}
                loading={busyKey === "add"}
                onClick={addViewer}
              >
                {t("add")}
              </Button>
            </Flex>
            <Group>
              <Button variant="default" onClick={cancelManaging}>
                {t("cancel")}
              </Button>
            </Group>
          </Stack>
        </Can>
      )}
    </Stack>
  );
}

